import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    IconButton,
    Stack,
    MenuItem,
    Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAuthStore } from "../../../store/auth.store";
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { FormDataSubmit } from "../../../types/Store/OrganizationStore.types";

import imageCompression from "browser-image-compression";
import { FeedbacksReasons } from "../../../configs/feedbacks-reasons.config";

type Props = {
    open: boolean;
    onClose: () => void;
    onSubmit?: (data: FormDataSubmit) => void;
    loading: boolean;
};

const reasons = Object.keys(FeedbacksReasons).map((n) => {
    return [FeedbacksReasons[n], n]
})

export default function TicketsDialog({
    open,
    onClose,
    onSubmit,
    loading,
}: Props) {
    const { user, accounts, activeAccount } = useAuthStore();

    const activeAcc = useMemo(() => {
        return accounts?.find((acc) => acc.id === activeAccount);
    }, [accounts, activeAccount]);

    const [fileLoading, setFileLoading] = useState(false)

    const [data, setData] = useState<FormDataSubmit>({
        name: user?.first_name || "",
        account: activeAcc?.external_id || "",
        provider: "",
        reason: reasons[0][1],
        title: "",
        description: "",
        file: null,
    });

    useEffect(() => {
        setData({ ...data, account: activeAcc?.external_id || "" });
    }, [accounts, activeAccount]);

    const descMax = 4000;
    const [touched, setTouched] = useState<Record<keyof FormDataSubmit, boolean>>(
        {} as any
    );
    const setField =
        <K extends keyof FormDataSubmit>(k: K) =>
        (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setData((prev) => ({ ...prev, [k]: e.target.value }));

    const errors = {
        name: !data.name && touched.name ? "Обязательное поле" : "",
        account: !data.account && touched.account ? "Обязательное поле" : "",
        title: !data.title && touched.title ? "Обязательное поле" : "",
        description:
            !data.description && touched.description
                ? "Обязательное поле"
                : data.description.length > descMax
                ? `Не более ${descMax} символов`
                : "",
    };

    const hasErrors = Object.values(errors).some(Boolean);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        // помечаем все поля как "touched", чтобы почказать ошибки
        setTouched({
            name: true,
            account: true,
            provider: true,
            reason: true,
            title: true,
            description: true,
        } as any);

        console.log(errors)

        if (hasErrors) return;

        await onSubmit?.(data);
        //onClose();
        setData({
            name: user?.first_name || "",
            account: activeAcc?.external_id || "",
            provider: "",
            reason: reasons[0][1],
            title: "",
            description: "",
            file: null,
        });
    };

    const handleFileInput = async (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files?.length == 1) {
            try {
                setFileLoading(true)
                const compressedFile = await imageCompression(
                    event.target.files[0],
                    { maxSizeMB: 1, useWebWorker: true, maxWidthOrHeight: 1920 }
                );

                setData({ ...data, file: compressedFile });
            } catch (error) {
                console.log(error);
            } finally{
                setFileLoading(false)
            }
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ pr: 6 }}>
                Новое обращение
                <IconButton
                    aria-label="Закрыть"
                    onClick={onClose}
                    sx={{ position: "absolute", right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Box component="form" onSubmit={handleSubmit} noValidate>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <TextField
                            label="Ваше имя"
                            disabled
                            value={data.name}
                            onChange={setField("name")}
                            onBlur={() =>
                                setTouched((t) => ({ ...t, name: true }))
                            }
                            error={!!errors.name}
                            helperText={errors.name}
                            fullWidth
                            required
                            autoFocus
                        />

                        <TextField
                            label="Лицевой счёт"
                            value={data.account}
                            onChange={setField("account")}
                            onBlur={() =>
                                setTouched((t) => ({ ...t, account: true }))
                            }
                            error={!!errors.account}
                            helperText={errors.account}
                            fullWidth
                            required
                            inputProps={{ inputMode: "numeric" }}
                            disabled
                        />

                        {/* <TextField
                            label="Исполнитель коммунальных услуг"
                            placeholder="Исполнитель коммунальных услуг или его ИНН"
                            value={data.provider}
                            onChange={setField("provider")}
                            fullWidth
                        /> */}

                        {/* <TextField
                            select
                            label="Причина"
                            value={data.reason}
                            onChange={setField("reason")}
                            fullWidth
                        >
                            {reasons.map((r) => (
                                <MenuItem key={r[1]} value={r[1]}>
                                    {r[0]}
                                </MenuItem>
                            ))}
                        </TextField> */}

                        <TextField
                            label="Заголовок"
                            value={data.title}
                            onChange={setField("title")}
                            onBlur={() =>
                                setTouched((t) => ({ ...t, title: true }))
                            }
                            error={!!errors.title}
                            helperText={errors.title}
                            fullWidth
                            required
                        />

                        <Box>
                            <TextField
                                label="Описание"
                                value={data.description}
                                onChange={(e) => {
                                    const v = e.target.value.slice(0, descMax);
                                    setData((prev) => ({
                                        ...prev,
                                        description: v,
                                    }));
                                }}
                                onBlur={() =>
                                    setTouched((t) => ({
                                        ...t,
                                        description: true,
                                    }))
                                }
                                error={!!errors.description}
                                helperText={
                                    errors.description ||
                                    `${data.description.length}/${descMax}`
                                }
                                multiline
                                minRows={4}
                                fullWidth
                                required
                            />
                        </Box>

                        {/* <TextField
                            label="Адрес электронной почты"
                            value={data.email}
                            onChange={setField("email")}
                            onBlur={() =>
                                setTouched((t) => ({ ...t, email: true }))
                            }
                            error={!!errors.email}
                            helperText={
                                errors.email ||
                                "На этот адрес будет отправлен ответ"
                            }
                            type="email"
                            fullWidth
                            required
                        /> */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "16px",
                            }}
                        >
                            <input
                                accept="image/*"
                                style={{ display: "none" }}
                                id="raised-button-file"
                                type="file"
                                onChange={handleFileInput}
                            />
                            <label htmlFor="raised-button-file">
                                <Button variant="outlined" component="span" disabled={fileLoading}>
                                    {fileLoading ? "Загрузка..." : "Загрузить фото"}
                                </Button>
                            </label>
                            {data.file && (
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        setData({ ...data, file: null });
                                    }}
                                >
                                    Убрать фото
                                </Button>
                            )}
                        </Box>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={onClose} variant="text">
                        Отмена
                    </Button>
                    <Button
                        variant="contained"
                        type="submit"
                        disabled={loading || fileLoading}
                    >
                        Отправить
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
}
